import store from '@/app/app-state';
import { copy, removeUndefinedValues } from '@/helpers/object';
import { getMaxSalaryValue, getMinSalaryValue } from '@/helpers/salary';
import { escapeHtml } from '@/helpers/string';

/* eslint-disable @typescript-eslint/camelcase */
export const getDefaultFilters = () => ({
  keywords: '',
  city: '',
  radius: store.getters.radiusOptions[0],
});

export const getDefaultRawDataValues = (requestedCountry) => {
  const country = store.getters['country/getCountryByCountryValue'](requestedCountry);
  const salaryOptions = store.getters['jobSearch/salaryOptionsFromFacets'];

  return {
    location_country: country.value,
    location_radius: store.getters.radiusOptions[0].value,
    minSalary: salaryOptions[0].value,
    maxSalary: salaryOptions[salaryOptions.length - 1].value,
    keywords: '',
    location: '',
    filter_by_channels: '',
    filter_by_locations: '',
    filter_by_branches: '',
    filter_by_careerLevels: '',
    filter_by_daysReleased: '',
    filter_by_recruiter_types: '',
    filter_by_company_id: '',
    filter_by_company_name: '',
    search_by_branches: '',
    search_by_channels: '',
    promotedJobs: '',
    featuredJobs: '',
    desiredJobTitle: null,
    initialSearchId: '',
    refinedSearchId: '',
    origin: 1,
    searchStartIndex: 0,
    searchLimit: 25,
    json: 1,
  };
};

export const getSalaryValue = ({ minSalary, maxSalary }) => {
  if (minSalary === undefined || maxSalary === undefined) {
    return undefined;
  }
  const salaryOptionsFromFacets = store.getters['jobSearch/salaryOptionsFromFacets'];

  return [
    salaryOptionsFromFacets.find((salaryOption) => salaryOption.value === Number(minSalary)),
    salaryOptionsFromFacets.find((salaryOption) => salaryOption.value === Number(maxSalary)),
  ];
};

// eslint-disable-next-line import/prefer-default-export
export const rawDataToMainFilters = (
  { keywords, location, location_radius, location_country, minSalary, maxSalary, desiredJobTitle },
  shouldRemoveUndefined = false,
) => {
  const salaryValue = getSalaryValue({
    minSalary,
    maxSalary,
  });

  const mainFilters = {
    keywords,
    city: location,
    radius: store.getters.radiusOptions.find(
      (radiusOption) => radiusOption.value === Number(location_radius),
    ),
    country: store.getters['country/getCountryByCountryValue'](location_country),
    salary: salaryValue && copy(salaryValue),
    desiredJobTitle: desiredJobTitle,
  };

  if (shouldRemoveUndefined) {
    return removeUndefinedValues(mainFilters);
  }

  return mainFilters;
};
/* eslint-enable @typescript-eslint/camelcase */

export const filtersToRawData = ({
  keywords,
  city,
  radius,
  country,
  salary,

  sources,
  cities,
  companyNames,
  careerLevels,
  branches,
  date,
  channels,
  company,
  desiredJobTitle,
}) => {
  /* eslint-disable @typescript-eslint/camelcase */
  const defaultValues = getDefaultRawDataValues(
    country?.value || store.getters['country/currentCountry'].value,
  );

  return {
    ...defaultValues,
    location_radius: radius?.value || 0,
    minSalary: getMinSalaryValue({ salary, defaultValue: defaultValues.minSalary }),
    maxSalary: getMaxSalaryValue({ salary, defaultValue: defaultValues.maxSalary }),
    keywords: keywords || '',
    location: city || '',
    desiredJobTitle: desiredJobTitle,
    filter_by_channels:
      channels
        ?.flatMap((functionalArea) => functionalArea.values)
        .map((channel) => channel.value)
        .join(',') || '',
    filter_by_locations: cities?.map((location) => location.value).join(',') || '',
    filter_by_branches: branches?.map((branch) => branch.value).join(',') || '',
    filter_by_careerLevels: careerLevels?.map((careerLevel) => careerLevel.value).join(',') || '',
    // eslint-disable-next-line no-restricted-globals
    filter_by_daysReleased: date && !isNaN(Number(date)) ? Number(date) : 31,
    filter_by_recruiter_types: sources?.map((source) => source.value).join(',') || '',
    filter_by_company_id: company?.value ? company.value.toString() : '',
    filter_by_company_name: companyNames?.map((companyName) => companyName.value).join('###') || '',
  };
  /* eslint-enable @typescript-eslint/camelcase */
};

export const mainFiltersToRawData = ({
  keywords,
  location,
  location_radius,
  country,
  salary,

  sources,
  cities,
  companyNames,
  careerLevels,
  branches,
  date,
  channels,
  company,
  desiredJobTitle,
}) => {
  /* eslint-disable @typescript-eslint/camelcase */
  const defaultValues = getDefaultRawDataValues(
    country?.value || store.getters['country/currentCountry'].value,
  );

  return {
    country: country?.value || store.getters['country/currentCountry'].value,
    location_radius: location_radius || 0,
    minSalary: getMinSalaryValue({ salary, defaultValue: defaultValues.minSalary }),
    maxSalary: getMaxSalaryValue({ salary, defaultValue: defaultValues.maxSalary }),
    keywords: keywords || '',
    location: location || '',
    desiredJobTitle: desiredJobTitle,
    filter_by_channels:
      channels
        ?.flatMap((functionalArea) => functionalArea.values)
        .map((channel) => channel.value)
        .join(',') || '',
    filter_by_locations: cities?.map((location) => location.value).join(',') || '',
    filter_by_branches: branches?.map((branch) => branch.value).join(',') || '',
    filter_by_careerLevels: careerLevels?.map((careerLevel) => careerLevel.value).join(',') || '',
    // eslint-disable-next-line no-restricted-globals
    filter_by_daysReleased: date && !isNaN(Number(date)) ? Number(date) : 31,
    filter_by_recruiter_types: sources?.map((source) => source.value).join(',') || '',
    filter_by_company_id: company?.value ? company.value.toString() : '',
    filter_by_company_name: companyNames?.map((companyName) => companyName.value).join('###') || '',
  };
  /* eslint-enable @typescript-eslint/camelcase */
};

export const encodeExposedFilterParams = (filters = {}) => ({
  ...filters,
  keywords: filters.keywords ? escapeHtml(filters.keywords) : '',
  city: filters.city ? escapeHtml(filters.city) : '',
});

export const replaceSalaryWithProperCountryCodeFromFacets = ({ minSalary, maxSalary }) => {
  const salaryOptionsFromFacets = store.getters['jobSearch/salaryOptionsFromFacets'];

  return [
    salaryOptionsFromFacets.find((salaryOption) => salaryOption.value === Number(minSalary)) ||
      salaryOptionsFromFacets[0],
    salaryOptionsFromFacets.find((salaryOption) => salaryOption.value === Number(maxSalary)) ||
      salaryOptionsFromFacets[salaryOptionsFromFacets.length - 1],
  ];
};
